import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { dramas, featuredDramaIds } from "@/mocks/dramas";
import type { Drama } from "@/types";

const slides: Drama[] = featuredDramaIds
  .map((id) => dramas.find((drama) => drama.id === id))
  .filter((drama): drama is Drama => Boolean(drama));

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setActive((index + slides.length) % slides.length);
  }, []);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const current = slides[active];

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (delta < -50) next();
        if (delta > 50) prev();
        touchStartX.current = null;
      }}
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={slide.banner}
            alt={`${slide.title}主视觉`}
            title={`${slide.title} - 仙剑奇侠传影视站`}
            className={`w-full h-full object-cover object-top ${
              index === active ? "animate-slow-zoom" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-end md:items-center">
        <div className="w-full pb-16 md:pb-0 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div key={current.id} className="lg:col-span-7 animate-float-in">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/90 text-foreground-950 text-xs font-medium">
              <i className="ri-fire-fill"></i>
              {current.category} · 热度 {current.heat}
            </span>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-semibold text-white leading-tight">
              {current.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-300"></i>
                {current.rating.toFixed(1)} 分
              </span>
              <span className="w-px h-4 bg-white/30"></span>
              <span>{current.year} 年首播</span>
              <span className="w-px h-4 bg-white/30"></span>
              <span>全 {current.episodes} 集</span>
              <span className="w-px h-4 bg-white/30"></span>
              <span>{current.region}地区</span>
            </div>
            <p className="mt-4 text-sm md:text-base text-background-100/90 leading-relaxed max-w-2xl">
              {current.summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-700 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                查看剧情
              </Link>
              <Link
                to={`/play/${current.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-400 text-foreground-950 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-play-circle-line"></i>
                播放演示
              </Link>
            </div>
          </div>

          <div className="hidden lg:flex lg:col-span-5 flex-col gap-3">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => goTo(index)}
                className={`flex items-center gap-4 p-3 rounded-lg text-left transition-colors cursor-pointer ${
                  index === active
                    ? "bg-white/15 backdrop-blur"
                    : "bg-black/20 hover:bg-white/10"
                }`}
              >
                <span className="w-14 h-16 rounded-md overflow-hidden shrink-0 bg-black/30">
                  <img
                    src={slide.poster}
                    alt={`${slide.title}海报`}
                    className="w-full h-full object-cover object-top"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-white truncate">
                    {slide.title}
                  </span>
                  <span className="block text-xs text-background-200 truncate mt-1">
                    {slide.year} · {slide.category}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute z-10 bottom-6 left-1/2 -translate-x-1/2 lg:left-8 lg:translate-x-0 flex items-center gap-2">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            aria-label={`切换到${slide.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-8 bg-accent-400" : "w-3 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      <div className="absolute z-10 bottom-6 right-4 md:right-8 flex items-center gap-2">
        <button
          type="button"
          onClick={prev}
          aria-label="上一张"
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一张"
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}